import { useEffect, useState } from 'react';
import { getClaims, isAuthenticated, redirectToLoginSync } from './authClient.js';
import { listTenants } from '../api/tenants.js';

const CUSTOMER_SPA_DOMAIN = 'app.dev.zen.zervizdev.com';

interface RedirectState {
  status: 'checking' | 'platform-ops' | 'redirecting' | 'no-tenant' | 'lookup-failed';
  url?: string;
  tenantId?: string;
}

/**
 * AuthGuard role-aware.
 *
 * - Si el user no está autenticado → redirect a Cognito hosted UI.
 * - Si el user es `platform-ops` → permite acceso a la admin de tenants
 *   (este builder-spa).
 * - Si el user es solo `admin` (tenant-admin) → resuelve el slug del tenant
 *   y redirige a `https://{slug}.app.dev.zen.zervizdev.com/`.
 * - Si no tiene rol válido → muestra mensaje claro.
 */
export function AuthGuard({ children }: { children: React.ReactNode }): JSX.Element | null {
  const authed = isAuthenticated();
  const claims = authed ? getClaims() : null;
  const [state, setState] = useState<RedirectState>({ status: 'checking' });

  useEffect(() => {
    if (!authed) {
      redirectToLoginSync();
      return;
    }
    if (!claims) {
      setState({ status: 'no-tenant' });
      return;
    }
    // platform-ops puede usar este admin.
    const claimsAny = claims as { roles?: string[]; groups: string[]; tenantId: string | null };
    const allRoles = [
      ...(claimsAny.roles ?? []),
      ...(claimsAny.groups ?? []),
    ];
    if (allRoles.includes('platform-ops')) {
      setState({ status: 'platform-ops' });
      return;
    }
    // tenant-admin → resolvemos slug y redirigimos.
    if (!claims.tenantId) {
      setState({ status: 'no-tenant' });
      return;
    }
    void listTenants({ limit: 200 })
      .then((res) => {
        const tn = res.items.find((t) => t.id === claims.tenantId || t.slug === claims.tenantId);
        if (!tn || !tn.slug) {
          setState({ status: 'lookup-failed', tenantId: claims.tenantId ?? undefined });
          return;
        }
        const url = `https://${tn.slug}.${CUSTOMER_SPA_DOMAIN}/`;
        setState({ status: 'redirecting', url });
        window.location.assign(url);
      })
      .catch(() => {
        setState({ status: 'lookup-failed', tenantId: claims.tenantId ?? undefined });
      });
  }, [authed, claims]);

  if (!authed || state.status === 'checking') return null;
  if (state.status === 'platform-ops') return <>{children}</>;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-sm">
      <div className="w-full max-w-md space-y-4 rounded border border-slate-200 bg-white p-6 shadow-sm">
        {state.status === 'redirecting' && (
          <>
            <h2 className="text-base font-semibold text-slate-800">
              Redirigiendo a tu workspace…
            </h2>
            <p className="text-slate-600">
              Tu cuenta administra un tenant cliente. Te estamos llevando a la app del cliente.
            </p>
            {state.url && (
              <a
                href={state.url}
                className="block rounded bg-brand-500 px-3 py-2 text-center text-white hover:bg-brand-600"
              >
                Ir a {state.url}
              </a>
            )}
          </>
        )}
        {state.status === 'no-tenant' && (
          <>
            <h2 className="text-base font-semibold text-red-700">
              Sesión sin tenant asignado
            </h2>
            <p className="text-slate-600">
              Tu cuenta no tiene un tenant válido. Contactá al equipo platform-ops.
            </p>
          </>
        )}
        {state.status === 'lookup-failed' && (
          <>
            <h2 className="text-base font-semibold text-amber-700">
              No pudimos resolver tu workspace
            </h2>
            <p className="text-slate-600">
              No encontramos un tenant con id <span className="font-mono">{state.tenantId}</span>.
              Si sos tenant-admin, contactá a soporte.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
